import { QuoteChangeState } from '@/enums/quote-change-state.enum';
import { IPolicyHolderDetails } from '@/interfaces/insurance-quote/policyholder.interface';
import { Address, InsuranceQuoteType, IQuote, RACQAddressResult } from '@/interfaces';
import { convertISOToDateFormatted, convertToISODate } from '@/utils/data-transforms';
import { AUSTRALIA, AUSTRALIA_ISO, INTERNATIONAL, MANUAL_ADDRESS_PLACEHOLDER } from '@/constants/general-words.const';
import { normalizeCsv, toSafeDisplayPostcode } from '@/utils/convert';
import { NIL as emptyGuid } from 'uuid';

const minDate = new Date('1901-01-01T00:00:00Z');

/**
 * Map policyholder helper
 *
 * @param policyholder
 * @param formData
 */
export const mapPolicyholder = (policyholder: IPolicyHolderDetails, formData: Record<string, any>) => {
  if (policyholder?.emailAddress?.value != formData.email) {
    policyholder.emailAddress = {
      value: formData.email,
      state: QuoteChangeState.Changed,
    };
  }
  if (policyholder?.firstname?.value != formData.fullname?.firstName) {
    policyholder.firstname = {
      value: formData.fullname?.firstName,
      state: QuoteChangeState.Changed,
    };
  }
  if (policyholder?.surname?.value != formData.fullname?.lastName) {
    policyholder.surname = {
      value: formData.fullname?.lastName,
      state: QuoteChangeState.Changed,
    };
  }
  if (policyholder?.salutation?.value != formData.fullname?.title) {
    policyholder.salutation = {
      value: formData.fullname?.title,
      state: QuoteChangeState.Changed,
    };
  }

  if (policyholder?.dateOfBirth?.value != convertToISODate(formData.dateOfBirth)) {
    policyholder.dateOfBirth = {
      value: convertToISODate(formData.dateOfBirth),
      state: QuoteChangeState.Changed,
    };
  }
  if (formData.gender && policyholder?.gender?.value != formData.gender) {
    policyholder.gender = {
      value: formData.gender,
      state: QuoteChangeState.Changed,
    };
  }
  if (policyholder?.telephoneNumber?.value != formData.primaryContactNumber) {
    policyholder.telephoneNumber = {
      value: formData.primaryContactNumber,
      state: QuoteChangeState.Changed,
    };
  }
  if (policyholder?.secondaryTelephoneNumber?.value != formData.secondaryContactNumber) {
    policyholder.secondaryTelephoneNumber = {
      value: formData.secondaryContactNumber,
      state: QuoteChangeState.Changed,
    };
  }

  // Postal Address
  if (policyholder?.postcode?.value != formData.postalAddress?.postcode) {
    policyholder.postcode = {
      value: formData.postalAddress?.postcode,
      state: QuoteChangeState.Changed,
    };
  }
  if (policyholder?.suburb?.value != formData.postalAddress?.suburb) {
    policyholder.suburb = {
      value: formData.postalAddress?.suburb,
      state: QuoteChangeState.Changed,
    };
  }
  if (policyholder?.street?.value != formData.postalAddress?.street) {
    policyholder.street = {
      value: formData.postalAddress?.street,
      state: QuoteChangeState.Changed,
    };
  }
  if (policyholder?.streetNumber?.value != formData.postalAddress?.streetNumber) {
    policyholder.streetNumber = {
      value: formData.postalAddress?.streetNumber,
      state: QuoteChangeState.Changed,
    };
  }
  if (policyholder?.country?.value != formData.postalAddress?.country) {
    policyholder.country = {
      value: formData.postalAddress?.country?.toUpperCase(),
      state: QuoteChangeState.Changed,
    };
  }
  if (policyholder?.unitNumber?.value != formData.postalAddress?.unitNumber) {
    policyholder.unitNumber = {
      value: formData.postalAddress?.unitNumber,
      state: QuoteChangeState.Changed,
    };
  }
  if (policyholder?.state?.value != formData.postalAddress?.state) {
    policyholder.state = {
      value: formData.postalAddress?.state,
      state: QuoteChangeState.Changed,
    };
  }
  if (policyholder?.dpid?.value != formData.postalAddress?.dpid) {
    policyholder.dpid = {
      value: formData.postalAddress?.dpid,
      state: QuoteChangeState.Changed,
    };
  }
  if (policyholder?.buildingLevel?.value != formData.postalAddress?.buildingLevel) {
    policyholder.buildingLevel = {
      value: formData.postalAddress?.buildingLevel,
      state: QuoteChangeState.Changed,
    };
  }
};

/**
 * Reverse policyholder from quote to form data
 *
 * @param policyholder
 * @param formData
 */
export const reversePolicyholder = (
  policyholder: IPolicyHolderDetails,
  formData: Record<string, any>,
  options: {
    autoAddressDefaultText?: string;
    postalAddressCompId?: string;
  }
) => {
  formData.email = policyholder?.emailAddress.value;
  formData.fullname = {
    firstName: policyholder?.firstname.value,
    lastName: policyholder?.surname.value,
    title: policyholder?.salutation.value,
  };

  if (policyholder?.dateOfBirth.value && policyholder?.dateOfBirth.value.getFullYear() > minDate.getUTCFullYear()) {
    formData.dateOfBirth = convertISOToDateFormatted(policyholder?.dateOfBirth?.value);
  }

  formData.gender = policyholder?.gender.value;
  formData.primaryContactNumber = policyholder?.telephoneNumber.value;
  formData.secondaryContactNumber = policyholder?.secondaryTelephoneNumber?.value;

  if (policyholder?.street.value) {
    const overseaFlag = options.postalAddressCompId
      ? `postalOverseas-${options.postalAddressCompId}`
      : 'postalOverseas';
    formData[overseaFlag] =
      policyholder?.country.value && ['AUSTRALIA', 'AUS'].includes(policyholder?.country.value?.toUpperCase())
        ? 'false'
        : 'true';

    formData.postalAddress_is_manual = !policyholder?.dpid.value;
    formData.postalAddress = reversePolicyholderPostalAddress(policyholder);

    if (!formData.postalAddress_is_manual) {
      formData.postalAddress_auto = joinAddressComponents(formData.postalAddress);
    } else {
      formData.postalAddress_auto = options.autoAddressDefaultText ?? MANUAL_ADDRESS_PLACEHOLDER;
    }
  }

  formData.policyHolderId = policyholder?.policyHolderId;
};

export const reversePolicyholderPostalAddress = (policyholder: IPolicyHolderDetails): Partial<RACQAddressResult> => {
  return {
    postcode: policyholder?.postcode?.value,
    suburb: policyholder?.suburb?.value,
    street: policyholder?.street?.value,
    streetNumber: policyholder?.streetNumber?.value,
    country: policyholder?.country?.value,
    unitNumber: policyholder?.unitNumber?.value,
    state: policyholder?.state?.value,
    dpid: policyholder?.dpid?.value?.toString(),
    buildingLevel: policyholder?.buildingLevel?.value,
  } as Partial<RACQAddressResult>;
};

export const joinAddressComponents = (postAddress: Record<string, string>) => {
  const isOverseas = postAddress.country && postAddress.country != AUSTRALIA;
  const addressParts = [];
  const numbersComponent = [];

  //Handle numbers separately otherwise the join(', ') below formats incorrectly.
  if (postAddress.unitNumber && postAddress.unitNumber.trim().length > 0) numbersComponent.push(postAddress.unitNumber);
  if (postAddress.buildingLevel && postAddress.buildingLevel.trim().length > 0)
    numbersComponent.push(postAddress.buildingLevel.trim());
  if (postAddress.streetNumber && postAddress.streetNumber.trim().length > 0)
    numbersComponent.push(postAddress.streetNumber.trim());

  addressParts.push(numbersComponent.join('/'));

  if (postAddress.street && postAddress.street.trim().length > 0) addressParts.push(postAddress.street + ',');
  if (postAddress.suburb && postAddress.suburb.trim().length > 0) addressParts.push(postAddress.suburb.toUpperCase());
  if (postAddress.state && postAddress.state.trim().length > 0 && !isOverseas) addressParts.push(postAddress.state);
  if (postAddress.postcode && postAddress.postcode.trim().length > 0) addressParts.push(postAddress.postcode);
  if (postAddress.country && postAddress.country.trim().length > 0 && isOverseas)
    addressParts.push(postAddress.country);

  return `${addressParts.shift()} ${addressParts.join(' ')}`;
};

export const joinAddressComponentsForPolicyholder = (
  policyholder: IPolicyHolderDetails,
  isPostalAddress: boolean = false,
  includeCountry: boolean = false
) => {
  let joinedAddress;

  const postCode = toSafeDisplayPostcode(policyholder.postcode?.value);
  const countryName =
    (policyholder.country?.value?.toUpperCase() !== AUSTRALIA.toUpperCase() &&
      policyholder.country?.value?.toUpperCase() !== AUSTRALIA_ISO.toUpperCase()) ||
    includeCountry
      ? policyholder.country?.value?.toUpperCase()
      : '';

  if (isPostalAddress) {
    joinedAddress = policyholder?.street?.value
      ? [
          policyholder.unitNumber?.value,
          policyholder.buildingLevel?.value,
          policyholder.streetNumber.value,
          policyholder.street.value + ',',
          policyholder.suburb?.value?.toUpperCase(),
          policyholder.state?.value?.toUpperCase(),
          postCode,
          countryName,
        ]
          .filter((o) => o)
          .join(' ')
      : '';
  } else {
    joinedAddress = [
      policyholder.unitNumber?.value,
      policyholder.buildingLevel?.value,
      policyholder.streetNumber?.value,
      policyholder.street?.value + ',',
      policyholder.suburb?.value,
      policyholder.state?.value,
      postCode,
      includeCountry && countryName,
    ]
      .filter((o) => o)
      .join(' ');
  }

  return normalizeCsv(joinedAddress);
};

/**
 * clear all properties of a policyholder.
 * @param policyholder
 */
export const clearPolicyholder = (policyholder: IPolicyHolderDetails) => {
  policyholder.emailAddress = {
    value: '',
    state: QuoteChangeState.Changed,
  };

  policyholder.firstname = {
    value: '',
    state: QuoteChangeState.Changed,
  };
  policyholder.surname = {
    value: '',
    state: QuoteChangeState.Changed,
  };
  policyholder.salutation = {
    value: '',
    state: QuoteChangeState.Changed,
  };
  policyholder.dateOfBirth = {
    value: minDate,
    state: QuoteChangeState.Changed,
  };
  policyholder.gender = {
    value: '',
    state: QuoteChangeState.Changed,
  };
  policyholder.telephoneNumber = {
    value: '',
    state: QuoteChangeState.Changed,
  };
  policyholder.secondaryTelephoneNumber = {
    value: '',
    state: QuoteChangeState.Changed,
  };

  // Postal Address
  policyholder.postcode = {
    value: '',
    state: QuoteChangeState.Changed,
  };
  policyholder.suburb = {
    value: '',
    state: QuoteChangeState.Changed,
  };
  policyholder.street = {
    value: '',
    state: QuoteChangeState.Changed,
  };
  policyholder.streetNumber = {
    value: '',
    state: QuoteChangeState.Changed,
  };
  policyholder.country = {
    value: '',
    state: QuoteChangeState.Changed,
  };
  policyholder.unitNumber = {
    value: '',
    state: QuoteChangeState.Changed,
  };
  policyholder.state = {
    value: '',
    state: QuoteChangeState.Changed,
  };
  policyholder.dpid = {
    value: undefined,
    state: QuoteChangeState.Changed,
  };
  policyholder.buildingLevel = {
    value: '',
    state: QuoteChangeState.Changed,
  };

  policyholder.hasRacqMembership = {
    value: false,
    state: QuoteChangeState.Changed,
  };

  policyholder.hasRoadsideProduct = {
    value: false,
    state: QuoteChangeState.Changed,
  };

  policyholder.highestRoadsideProduct = {
    value: '',
    state: QuoteChangeState.Changed,
  };

  policyholder.membershipEligibilityType = {
    value: '',
    state: QuoteChangeState.Changed,
  };

  policyholder.membershipLoyaltyLevel = {
    value: '',
    state: QuoteChangeState.Changed,
  };

  policyholder.reference = {
    value: '',
    state: QuoteChangeState.Changed,
  };

  policyholder.apttusId = {
    value: '',
    state: QuoteChangeState.Changed,
  };

  policyholder.policyHolderId = emptyGuid;

  policyholder.partyId = {
    value: emptyGuid,
    state: QuoteChangeState.Changed,
  };
};

/**
 * Map Address to RACQAddressResult
 * @param address
 */
export const mapAddressToRacqAddressResult = (address: Address): Partial<RACQAddressResult> | undefined => {
  if (!address?.dpid) return; // do not prefill it address is not verified

  return {
    addressLine: address?.formattedStreet,
    buildingLevel: address?.buildingLevel,
    unitNumber: address?.unit,
    streetNumber: address?.streetNumber,
    street: address?.street,
    suburb: address?.suburb,
    postcode: address?.postcode,
    state: address?.state,
    country: address?.country,
    dpid: address?.dpid,
  };
};

export const getPolicyHolderPostalAddress = (policyholder?: IPolicyHolderDetails): RACQAddressResult | undefined => {
  if (!policyholder) return undefined;

  return {
    postcode: policyholder.postcode?.value?.toString(),
    suburb: policyholder.suburb?.value?.toString(),
    street: policyholder.street?.value?.toString(),
    streetNumber: policyholder.streetNumber?.value?.toString(),
    country: policyholder.country?.value?.toUpperCase() ?? AUSTRALIA.toUpperCase(),
    unitNumber: policyholder.unitNumber?.value?.toString(),
    state: policyholder.state?.value?.toString(),
    dpid: policyholder.dpid?.value?.toString(),
    buildingLevel: policyholder.buildingLevel?.value?.toString(),
  };
};

export const getInsuredAddressFromQuote = (quote: Partial<IQuote>): RACQAddressResult | undefined => {
  if (!quote?.quoteType) return;

  if (quote.quoteType === InsuranceQuoteType.PetComp) {
    const pet = quote.petsQuote?.pets?.[0];
    if (!pet) return;

    return {
      postcode: pet.postcode?.value,
      suburb: pet.suburb?.value,
      street: pet.streetName?.value,
      streetNumber: pet.streetNumber?.value,
      buildingName: pet.buildingName?.value,
      country: 'AUSTRALIA',
      unitNumber: pet.unitNumber?.value,
      state: pet.state?.value,
      dpid: pet.dpid?.value?.toString(),
      buildingLevel: pet.buildingLevel?.value?.toString(),
    };
  }

  if (
    quote.quoteType === InsuranceQuoteType.Home ||
    quote.quoteType === InsuranceQuoteType.HomeAndContents ||
    quote.quoteType === InsuranceQuoteType.Contents
  ) {
    const location = quote.homeAndContentsQuote?.homeLocationDetails;
    if (!location) return;

    return {
      postcode: location.postCode.value,
      suburb: location.suburb.value,
      street: location.streetName.value,
      streetNumber: location.streetNumber.value,
      buildingName: location.buildingName.value,
      country: 'AUSTRALIA',
      unitNumber: location.unitNumber.value,
      state: location.state.value,
      dpid: location.dpid.value?.toString(),
      buildingLevel: location.buildingLevel?.value?.toString(),
    };
  }

  if (
    quote.quoteType === InsuranceQuoteType.MotorComprehensive ||
    quote.quoteType === InsuranceQuoteType.MotorComprehensiveOther ||
    (quote.motorQuote && quote.motorQuote.vehicleLocationDetails)
  ) {
    const motor = quote.motorQuote?.vehicleLocationDetails;
    if (!motor) return;

    return {
      postcode: motor.postcode?.value,
      suburb: motor.suburb?.value,
      street: motor.street?.value,
      streetNumber: motor.streetNumber?.value,
      buildingName: motor.buildingName?.value,
      country: 'AUSTRALIA',
      unitNumber: motor.unitNumber?.value,
      state: motor.state?.value,
      dpid: motor.dpid?.value?.toString(),
    };
  }

  return;
};

export const getPostcodeOrInternational = (primaryPolicyHolderDetails: IPolicyHolderDetails) => {
  if (
    primaryPolicyHolderDetails &&
    primaryPolicyHolderDetails?.country?.value?.toLowerCase() !== AUSTRALIA.toLowerCase() &&
    primaryPolicyHolderDetails?.country?.value?.toLowerCase() !== AUSTRALIA_ISO.toLowerCase() &&
    !primaryPolicyHolderDetails?.postcode?.value
  ) {
    return INTERNATIONAL;
  } else {
    return primaryPolicyHolderDetails?.postcode?.value;
  }
};

export const mapPrimaryPolicyholderAddress = (primaryPolicyHolderDetails: IPolicyHolderDetails) => {
  return {
    dpid: primaryPolicyHolderDetails?.dpid?.value,
    unit: primaryPolicyHolderDetails?.unitNumber?.value,
    buildingLevel: primaryPolicyHolderDetails?.buildingLevel?.value,
    streetNumber: primaryPolicyHolderDetails?.streetNumber?.value,
    street: primaryPolicyHolderDetails?.street?.value,
    suburb: primaryPolicyHolderDetails?.suburb?.value,
    postcode: primaryPolicyHolderDetails?.postcode?.value,
    state: primaryPolicyHolderDetails?.state?.value,
    country: primaryPolicyHolderDetails?.country?.value,
  };
};

/**
 * Map account creation step
 * @param quote
 * @param formData
 */
export const mapAccountCreation = (quote: Partial<IQuote>, formData: Record<string, any>) => {
  formData.policyholders.forEach((answers: Record<string, any>, index: number) => {
    const addressLookup = answers.addressLookup || {};

    const policyholder =
      quote.primaryPolicyHolderDetails?.policyHolderId == answers.policyHolderId
        ? quote.primaryPolicyHolderDetails
        : quote.additionalPolicyHolderDetails?.find((ph) => ph.policyHolderId == answers.policyHolderId);
    if (!policyholder) return;

    // Same as postal address
    if (answers.confirmResidentialAddress == 'true') {
      policyholder.residentialAddress = {
        dpid: policyholder.dpid?.value?.toString(),
        addressType: 'Residential',
        unit: policyholder.unitNumber?.value,
        streetNumber: policyholder.streetNumber?.value,
        street: policyholder.street?.value,
        suburb: policyholder.suburb?.value,
        postcode: policyholder.postcode?.value,
        state: policyholder.state?.value,
        country: policyholder.country?.value?.toUpperCase(),
        buildingLevel: policyholder.buildingLevel?.value,
      };
    }
    // Map new residential address
    else {
      policyholder.residentialAddress = {
        dpid: addressLookup?.dpid,
        addressType: 'Residential',
        unit: addressLookup?.unit || addressLookup?.unitNumber,
        streetNumber: addressLookup?.streetNumber,
        street: addressLookup?.street,
        suburb: addressLookup?.suburb,
        postcode: addressLookup?.postcode,
        state: addressLookup?.state,
        country: addressLookup?.country?.toUpperCase(),
        buildingLevel: addressLookup?.buildingLevel,
      };
    }
  });
};

export const reverseMapAccountCreation = (quote: Partial<IQuote>) => {
  return [quote.primaryPolicyHolderDetails, ...(quote.additionalPolicyHolderDetails ?? [])]
    .filter((policyholder) => !policyholder?.apttusId?.value)
    .map((policyholder) => {
      if (!policyholder?.residentialAddress?.street) {
        return {};
      }

      return {
        confirmResidentialAddress: (policyholder?.residentialAddress?.dpid == policyholder?.dpid.value).toString(),
        addressLookup_auto: joinIQPolicyholderResidentialAddress(policyholder.residentialAddress),
        addressLookup_is_manual: !policyholder.residentialAddress?.dpid,
        addressLookup: {
          dpid: policyholder?.residentialAddress?.dpid,
          unitNumber: policyholder?.residentialAddress?.unit,
          streetNumber: policyholder?.residentialAddress?.streetNumber,
          street: policyholder?.residentialAddress?.street,
          suburb: policyholder?.residentialAddress?.suburb,
          postcode: policyholder?.residentialAddress?.postcode,
          state: policyholder?.residentialAddress?.state,
          country: policyholder?.residentialAddress?.country,
          buildingLevel: policyholder?.residentialAddress?.buildingLevel,
        },
        addressLookup_manual: {
          unitNumber: policyholder?.residentialAddress?.unit,
          streetNumber: policyholder?.residentialAddress?.streetNumber,
          street: policyholder?.residentialAddress?.street,
          suburb: policyholder?.residentialAddress?.suburb,
          postcode: policyholder?.residentialAddress?.postcode,
          state: policyholder?.residentialAddress?.state,
          country: policyholder?.residentialAddress?.country,
        },
        /** postalOverseas is hardcoded name */
        [`postalOverseas-${policyholder.policyHolderId}`]: ['AUSTRALIA', 'AUS'].includes(
          policyholder.residentialAddress.country?.toUpperCase() ?? ''
        )
          ? 'false'
          : 'true',
      };
    });
};

export const joinIQPolicyholderResidentialAddress = (address?: Address) => {
  // street in mandatory in all scenarios
  if (!address?.street) return '';
  const addressLine1 = [address.unit, address.buildingLevel, address.streetNumber, address.street]
    .filter((component) => !!component)
    .join(' ');
  const addressLine2 = [address.suburb?.toUpperCase(), address.state, address.postcode]
    .filter((component) => !!component)
    .join(' ');
  let joinedAddress = `${addressLine1}, ${addressLine2}`;

  // Include country for oversea address
  if (
    address.country &&
    ![AUSTRALIA.toLowerCase(), AUSTRALIA_ISO.toLowerCase()].includes(address.country.toLowerCase())
  ) {
    joinedAddress += ` ${address.country}`;
  }

  return normalizeCsv(joinedAddress);
};
